"use client";

import { BusinessListMobileSort } from "@/app/(saas)/(app)/businesses/_components/business-list-mobile-sort";
import { BusinessMobileFilters } from "@/app/(saas)/(app)/businesses/_components/business-mobile-filters";
import { LocationSearchBox } from "@/app/(saas)/(app)/businesses/_components/filters/location-search-box";
import type { BusinessesSearchView } from "@/app/(saas)/(app)/businesses/_components/businesses-view";
import { SelectedFilterCount } from "@/components/common/selected-filter-count";
import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

export function BusinessesMobileHeader<SortType extends string>({
  currentView,
  onChangeView,

  sortOptions,
  sortValue,
  onSortValueChange,
  isLoading,
  isLocationUpdating,
  collapsed,
}: {
  currentView: BusinessesSearchView;
  onChangeView: (view: BusinessesSearchView) => void;

  sortOptions: readonly SortType[];
  sortValue: SortType;
  onSortValueChange: (value: SortType) => void;
  isLoading?: boolean;
  isLocationUpdating?: boolean;
  collapsed?: boolean;
}) {
  const t = useTranslations();

  const [filters] = useAtom(businessFiltersAtom);

  const detailsFiltersCount = useMemo(() => {
    const nonDefaultRanges = [
      filters.establishmentAgeRange,
      filters.employeeCountRange,
      filters.ownerWorkedHoursRange,
    ].filter((range) => range?.min !== null || range?.max !== null);

    return (
      nonDefaultRanges.length +
      (filters.propertyIncluded ? 1 : 0) +
      filters.listedBy.length
    );
  }, [filters]);

  const financingFiltersCount = useMemo(() => {
    return [
      filters.financingAvailable,
      filters.sbaPrequalified,
      filters.visaQualified,
    ].filter(Boolean).length;
  }, [filters]);

  const financialsFiltersCount = useMemo(() => {
    return [
      filters.priceRange,
      filters.revenueRange,
      filters.profitRange,
      filters.sdeRange,
    ].filter((range) => range?.min !== null || range?.max !== null).length;
  }, [filters]);

  const filtersCount = useMemo(() => {
    return (
      detailsFiltersCount +
      financingFiltersCount +
      financialsFiltersCount +
      (filters.businessTypeIds?.length || 0)
    );
  }, [
    detailsFiltersCount,
    financingFiltersCount,
    financialsFiltersCount,
    filters.businessTypeIds,
  ]);

  if (collapsed) return null;

  return (
    <div className="border-b p-3 sm:p-4 xl:hidden">
      <div className="mb-3">
        <LocationSearchBox isLoading={isLocationUpdating} />
      </div>

      <div className="grid grid-cols-3 items-center gap-2 sm:gap-3">
        <BusinessMobileFilters>
          <Button variant="outline" className="h-11 w-full px-2 text-xs sm:text-sm">
            <Icon icon="ListFilter" />
            {filtersCount > 0 && <SelectedFilterCount count={filtersCount} />}
            {t("saas.search.mobile.filter")}
          </Button>
        </BusinessMobileFilters>

        <BusinessListMobileSort
          options={sortOptions}
          value={sortValue}
          onChange={(val) => onSortValueChange(val)}
          disabled={isLoading}
        >
          <Button variant="outline" className="h-11 w-full px-2 text-xs sm:text-sm">
            <Icon icon="ListStart" />
            {t("saas.search.mobile.sort")}
          </Button>
        </BusinessListMobileSort>

        <Button
          variant="outline"
          className="h-11 w-full px-2 text-xs sm:text-sm"
          onClick={() => onChangeView(currentView === "map" ? "list" : "map")}
        >
          <Icon icon={currentView === "map" ? "ListBullet" : "Map"} />
          {t(
            currentView === "map"
              ? "saas.search.mobile.list"
              : "saas.search.mobile.map",
          )}
        </Button>
      </div>
    </div>
  );
}
